import React, { useMemo } from 'react';
import { Bar, BarChart, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { Empty, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { LIGHT_COLORS } from '../constants';
import type { MetricKey, MetricScore, TrendPoint } from '../types';

const { Text } = Typography;

interface Props {
    points: TrendPoint[];
    redThreshold: number;
    yellowThreshold: number;
}

const METRIC_ORDER: MetricKey[] = ['pickupRate', 'deliveryRate', 'pickupWithin12hRate', 'deliveryWithin24hRate'];

const pickMetric = (metrics: MetricScore[], key: MetricKey): MetricScore | undefined =>
    metrics.find((m) => m.metric === key);

const renderRate = (metric: MetricScore | undefined) => {
    if (!metric || metric.actual === null) return <Text type="secondary">-</Text>;
    const below = metric.gap < 0;
    return (
        <Text type={below ? 'danger' : undefined} strong={below}>
            {metric.actual.toFixed(1)}
        </Text>
    );
};

const ScoreTrendTable: React.FC<Props> = ({ points, redThreshold, yellowThreshold }) => {
    const { t } = useTranslation();
    const I18N = 'dashboard.dailyMeeting';

    // 按日期升序展示柱图，表格按日期倒序展示明细（近的在上）
    const chartData = useMemo(
        () => [...points].sort((a, b) => a.statDate.localeCompare(b.statDate))
            .map((p) => ({ label: p.statDate.slice(5), score: p.totalScore, light: p.light })),
        [points],
    );
    const tableData = useMemo(
        () => [...points].sort((a, b) => b.statDate.localeCompare(a.statDate)),
        [points],
    );

    if (points.length === 0) {
        return <Empty description={t(`${I18N}.trend.empty`)} />;
    }

    const columns: ColumnsType<TrendPoint> = [
        {
            title: t(`${I18N}.trend.col.date`),
            dataIndex: 'statDate',
            width: 110,
        },
        {
            title: t(`${I18N}.trend.col.totalScore`),
            dataIndex: 'totalScore',
            width: 90,
            align: 'right',
            render: (value: number, row) => (
                <Text strong style={{ color: LIGHT_COLORS[row.light] }}>
                    {value.toFixed(1)}
                </Text>
            ),
        },
        {
            title: t(`${I18N}.trend.col.light`),
            dataIndex: 'light',
            width: 70,
            align: 'center',
            render: (light: TrendPoint['light']) => (
                <span
                    style={{
                        display: 'inline-block',
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: LIGHT_COLORS[light],
                    }}
                />
            ),
        },
        {
            title: t(`${I18N}.trend.col.arrivals`),
            dataIndex: 'actualArrivals',
            width: 90,
            align: 'right',
            render: (value: number) => Number(value).toLocaleString(),
        },
        ...METRIC_ORDER.map((key) => ({
            title: t(`${I18N}.metric.${key}`),
            key,
            width: 110,
            align: 'right' as const,
            render: (_: unknown, row: TrendPoint) => renderRate(pickMetric(row.metrics, key)),
        })),
    ];

    return (
        <div>
            <div className="mb-3">
                <Text type="secondary" className="text-xs">{t(`${I18N}.trend.chartTitle`)}</Text>
                <ResponsiveContainer width="100%" height={120}>
                    <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                        <Tooltip formatter={(value: number) => value.toFixed(1)} />
                        <ReferenceLine y={redThreshold} stroke={LIGHT_COLORS.RED} strokeDasharray="4 4" />
                        <ReferenceLine y={yellowThreshold} stroke={LIGHT_COLORS.YELLOW} strokeDasharray="4 4" />
                        <Bar dataKey="score">
                            {chartData.map((d, idx) => (
                                <Cell key={idx} fill={LIGHT_COLORS[d.light]} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <Table
                size="small"
                rowKey="statDate"
                columns={columns}
                dataSource={tableData}
                pagination={false}
                scroll={{ y: 420 }}
            />
        </div>
    );
};

export default ScoreTrendTable;
